import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API, getImageUrl } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import { NotificationBell, registerPushSubscription } from '../components/NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Activity,
  Calendar,
  TrendingUp,
  LogOut,
  Home,
  ClipboardCheck,
  Check,
  Clock,
  Target,
  BarChart3,
  RefreshCw,
  Building2,
  Loader2,
  Lock,
  AlertTriangle,
  AlertCircle,
  MessageCircle,
  Send,
  Users,
  UserPlus,
  Info,
  User,
  Camera,
  Save,
  Pencil,
  Dumbbell,
  MapPin,
  Phone,
  Mail,
  Search,
  Shield,
  Navigation,
  Ticket,
  Stethoscope,
  Sparkles
} from 'lucide-react';
import PlanBanner from '../components/PlanBanner';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from '../i18n';
import HistoryPage from './student/HistoryPage';
import EvolutionPage from './student/EvolutionPage';
import CoparticipationPage from './student/CoparticipationPage';
import ContactPage from './student/ContactPage';
import ProfessionalNetworkPage from './student/ProfessionalNetworkPage';
import MyVitalPassPage from './student/MyVitalPassPage';
import BenefitsPage from './student/BenefitsPage';
import PersonalOnboardingTour from '../components/PersonalOnboardingTour';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';

// Countdown timer for 2-minute check-in codes
const CodeCountdown = ({ expiresAt, onExpired }) => {
  const [timeLeft, setTimeLeft] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const update = () => {
      const diff = Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000));
      setTimeLeft(diff);
      if (diff <= 0) onExpired?.();
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, onExpired]);
  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;
  const isUrgent = timeLeft <= 30;
  return (
    <div className={`text-center ${isUrgent ? 'animate-pulse' : ''}`}>
      <p className={`font-mono text-2xl font-bold ${isUrgent ? 'text-red-500' : 'text-slate-600'}`}>
        {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
      </p>
      <p className={`text-xs mt-1 ${isUrgent ? 'text-red-400' : 'text-slate-400'}`}>
        {timeLeft > 0 ? 'Tempo restante' : 'Expirado!'}
      </p>
    </div>
  );
};

// Check-in Component
const CheckInPage = () => {
  const navigate = useNavigate();
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkinData, setCheckinData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [checkinCode, setCheckinCode] = useState(null);
  const [codeExpires, setCodeExpires] = useState(null);
  const [passCode, setPassCode] = useState(null);
  const [passExpires, setPassExpires] = useState(null);
  const [generatingPass, setGeneratingPass] = useState(false);
  const [gyms, setGyms] = useState([]);
  const [planData, setPlanData] = useState(null);
  const [selectedGym, setSelectedGym] = useState(null);
  const [bindingGym, setBindingGym] = useState(null);
  const [selectedModality, setSelectedModality] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Get user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setUserLocation(null),
        { enableHighAccuracy: false, timeout: 8000 }
      );
    }
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      let gymsUrl = `${API}/student/available-gyms`;
      if (userLocation) {
        gymsUrl += `?lat=${userLocation.lat}&lng=${userLocation.lng}`;
      }
      const [statusRes, gymsRes, planRes] = await Promise.all([
        axios.get(`${API}/checkins/today`),
        axios.get(gymsUrl).catch(() => ({ data: [] })),
        axios.get(`${API}/students/my-plan`).catch(() => ({ data: {} }))
      ]);
      setCheckedIn(statusRes.data.checked_in);
      setCheckinData(statusRes.data.checkin);
      setGyms(gymsRes.data);
      setPlanData(planRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [userLocation]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const primaryGymId = planData?.primary_gym_id;
  const primaryGym = gyms.find(g => g.gym_id === primaryGymId);
  const otherGyms = gyms.filter(g => g.gym_id !== primaryGymId);

  const studentPlanId = planData?.plan || null;
  const hasActivePlan = Boolean(studentPlanId);
  const gymAcceptsMyPlan = (gym) => {
    if (!studentPlanId) return false;
    const accepted = gym?.accepted_plans || [];
    if (accepted.length === 0) return false;
    return accepted.includes(studentPlanId);
  };

  const filteredGyms = (primaryGym ? otherGyms : gyms).filter(g => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (g.name || '').toLowerCase().includes(q) ||
           (g.city || '').toLowerCase().includes(q) ||
           (g.neighborhood || '').toLowerCase().includes(q) ||
           (g.modalities || []).some(m => m.toLowerCase().includes(q));
  });

  const daysUntilChange = () => {
    if (!planData?.primary_gym_set_at) return 0;
    const setDate = new Date(planData.primary_gym_set_at);
    return Math.max(0, Math.ceil((setDate.getTime() + 30 * 86400000 - Date.now()) / 86400000));
  };
  const remaining = daysUntilChange();

  const [bindModalities, setBindModalities] = useState([]);
  const [showBindConfirm, setShowBindConfirm] = useState(null);

  const handleGeneratePassCode = async () => {
    setGeneratingPass(true);
    try {
      const res = await axios.post(`${API}/student/generate-pass-code`);
      setPassCode(res.data.code);
      setPassExpires(res.data.expires_at);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar código');
    } finally {
      setGeneratingPass(false);
    }
  };

  const handleBindGym = async (gymId) => {
    if (!hasActivePlan) {
      toast.error('Adquira um plano antes de vincular a uma academia', {
        action: { label: 'Ver planos', onClick: () => navigate('/plans/student') },
      });
      return;
    }
    const gym = gyms.find(g => g.gym_id === gymId);
    if (gym && !gymAcceptsMyPlan(gym)) {
      toast.error('Esta academia não aceita seu plano atual. Escolha outra academia compatível.');
      return;
    }
    const gymMods = gym?.modalities || [];
    // If gym has 2+ modalities and none selected, show dialog
    if (gymMods.length >= 2 && bindModalities.length === 0) {
      setShowBindConfirm(gymId);
      return;
    }
    setBindingGym(gymId);
    try {
      const res = await axios.post(`${API}/students/set-primary-gym`, { gym_id: gymId, modalities: bindModalities });
      toast.success(res.data.message);
      setShowBindConfirm(null);
      setBindModalities([]);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao vincular academia');
    } finally {
      setBindingGym(null);
    }
  };

  const confirmBindWithModalities = async () => {
    const gymId = showBindConfirm;
    setBindingGym(gymId);
    try {
      const res = await axios.post(`${API}/students/set-primary-gym`, { gym_id: gymId, modalities: bindModalities });
      toast.success(res.data.message);
      setShowBindConfirm(null);
      setBindModalities([]);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao vincular academia');
    } finally {
      setBindingGym(null);
    }
  };

  const generateCode = async (gymId) => {
    if (!hasActivePlan) {
      toast.error('Adquira um plano antes de fazer check-in', {
        action: { label: 'Ver planos', onClick: () => navigate('/plans/student') },
      });
      return;
    }
    const gym = gyms.find(g => g.gym_id === gymId);
    if (gym && !gymAcceptsMyPlan(gym)) {
      toast.error('Esta academia não aceita seu plano atual.');
      return;
    }
    if (gym?.modalities?.length > 0 && !selectedModality) {
      toast.error('Selecione uma modalidade antes do check-in');
      return;
    }
    setGenerating(true);
    try {
      const payload = { gym_id: gymId };
      if (selectedModality) payload.modality = selectedModality;
      const res = await axios.post(`${API}/student/generate-checkin-code`, payload);
      setCheckinCode(res.data.code);
      setCodeExpires(res.data.expires_at);
      toast.success('Código gerado! Informe na recepção em até 2 minutos.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar código');
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectGym = (gym) => {
    if (selectedGym === gym.gym_id) {
      setSelectedGym(null);
      setSelectedModality(null);
    } else {
      setSelectedGym(gym.gym_id);
      setSelectedModality(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Check-in
        </h1>
        <p className="text-slate-600 mt-1">Selecione a academia e gere seu código de check-in</p>
      </div>

      {/* Already checked in */}
      {checkedIn ? (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-emerald-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Check-in Realizado!
            </h3>
            <p className="text-emerald-700">
              Hoje às {checkinData && new Date(checkinData.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </p>
            {checkinData?.modality && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 rounded-full text-sm text-blue-700 font-medium">
                {checkinData.modality}
              </div>
            )}
            {checkinData?.checkin_type === 'extra' && (
              <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 rounded-full text-sm text-amber-700 font-medium">
                <AlertTriangle className="w-3.5 h-3.5" /> Check-in Extra
              </div>
            )}
          </CardContent>
        </Card>
      ) : checkinCode ? (
        /* Code generated - show 5-char code with 2min timer */
        <Card className="border-slate-100">
          <CardContent className="p-8 text-center">
            <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Código de Check-in Extra
            </h3>
            <p className="text-slate-500 text-sm mb-4">Informe este código na recepção da academia</p>
            <div className="mb-3 inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 rounded-full text-sm text-amber-700 font-medium">
              <AlertTriangle className="w-3.5 h-3.5" /> Check-in Extra (academia sem vínculo)
            </div>
            <div className="bg-slate-900 rounded-2xl p-8 inline-block mb-4">
              <p className="font-mono text-5xl md:text-6xl font-black tracking-[0.4em] text-emerald-400" data-testid="checkin-code-display">
                {checkinCode}
              </p>
            </div>
            <CodeCountdown expiresAt={codeExpires} onExpired={() => { setCheckinCode(null); setCodeExpires(null); toast.error('Código expirado! Gere um novo.'); }} />
            <div className="mt-4">
              <Button onClick={() => { setCheckinCode(null); setCodeExpires(null); setSelectedGym(null); setSelectedModality(null); }} variant="outline">
                Voltar
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Gym selection + check-in */
        <>
          {/* Primary gym - DIGITAL PASS (no code generation needed) */}
          {primaryGym && (
            <Card className="border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 shadow-lg" data-testid="primary-gym-digital-pass">
              <CardContent className="p-4 sm:p-6">
                {/* Header badge */}
                <div className="flex items-center justify-center mb-4">
                  <div className="flex items-center gap-2 px-3 sm:px-4 py-1.5 bg-emerald-600 text-white rounded-full text-xs sm:text-sm font-bold uppercase tracking-wider">
                    <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Passe Digital — Academia Vínculo
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4">
                  {primaryGym.photo_path ? (
                    <img src={getImageUrl(primaryGym.photo_path)} alt={primaryGym.name} className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl object-cover flex-shrink-0 border-2 border-emerald-200" />
                  ) : (
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0 border-2 border-emerald-200">
                      <Dumbbell className="w-7 h-7 sm:w-9 sm:h-9 text-emerald-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0 text-center sm:text-left">
                    <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                      <h3 className="text-lg sm:text-xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{primaryGym.name}</h3>
                      {remaining > 0 && (
                        <span className="text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0">
                          <Lock className="w-3 h-3" /> Troca em {remaining}d
                        </span>
                      )}
                    </div>
                    {primaryGym.street && <p className="text-xs sm:text-sm text-slate-500 mt-1">{primaryGym.street}{primaryGym.number ? `, ${primaryGym.number}` : ''}</p>}
                    <div className="flex items-center justify-center sm:justify-start gap-3 mt-1.5 flex-wrap">
                      {primaryGym.distance_km != null && (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full" data-testid="primary-gym-distance">
                          <MapPin className="w-3.5 h-3.5" /> {primaryGym.distance_km} km
                        </span>
                      )}
                      {(primaryGym.address || primaryGym.street || primaryGym.city) && (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([primaryGym.street, primaryGym.number, primaryGym.neighborhood, primaryGym.city, primaryGym.state].filter(Boolean).join(', '))}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                          data-testid="primary-gym-maps-link"
                        >
                          <Navigation className="w-3 h-3" /> Ver no Maps
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bound modalities */}
                {(planData?.bound_modalities?.length > 0 || primaryGym.modalities?.length > 0) && (
                  <div className="mt-4 pt-4 border-t border-emerald-200">
                    <p className="text-xs font-semibold text-emerald-700 mb-2">Modalidades Vinculadas:</p>
                    <div className="flex flex-wrap gap-2">
                      {(planData?.bound_modalities?.length > 0 ? planData.bound_modalities : primaryGym.modalities || []).map(mod => (
                        <span key={mod} className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium">
                          <Check className="w-3.5 h-3.5" /> {mod}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pass validation code */}
                {passCode ? (
                  <div className="mt-4 pt-4 border-t border-emerald-200">
                    <div className="bg-slate-900 rounded-2xl p-4 sm:p-6 text-center">
                      <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Código de Validação</p>
                      <p className="font-mono text-4xl sm:text-5xl font-black tracking-[0.4em] sm:tracking-[0.5em] text-emerald-400" data-testid="pass-code-display">
                        {passCode}
                      </p>
                    </div>
                    <CodeCountdown expiresAt={passExpires} onExpired={() => { setPassCode(null); setPassExpires(null); toast.info('Código expirado. Gere um novo se necessário.'); }} />
                    <p className="text-xs text-emerald-600 text-center mt-2">Informe este código na recepção</p>
                    <div className="mt-3 text-center">
                      <Button onClick={() => { setPassCode(null); setPassExpires(null); }} variant="outline" size="sm">
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 pt-4 border-t border-emerald-200 space-y-3">
                    <div className="p-3 bg-emerald-100/60 rounded-xl text-center">
                      <p className="text-sm text-emerald-800 font-medium">Apresente esta tela na recepção da academia</p>
                      <p className="text-xs text-emerald-600 mt-1">Acesso direto sem necessidade de código</p>
                    </div>
                    <div className="text-center">
                      <Button
                        onClick={handleGeneratePassCode}
                        disabled={generatingPass}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="generate-pass-code-btn"
                      >
                        {generatingPass ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                        Validar Card
                      </Button>
                      <p className="text-[11px] text-slate-400 mt-1.5">Gera um código de 3 dígitos válido por 1 minuto</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* No primary gym warning */}
          {!primaryGym && gyms.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Você ainda não tem uma academia vínculo</p>
                <p className="text-xs text-amber-700 mt-1">Escolha abaixo para definir sua academia principal. Check-ins em outras academias serão contados como extra.</p>
              </div>
            </div>
          )}

          {/* Extra check-ins counter */}
          {planData?.plan && primaryGym && (
            <Card className="border-slate-100">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-900">Check-ins Extras Este Mês</p>
                  <p className="text-xs text-slate-500">Em academias sem vínculo</p>
                </div>
                <div className="text-right flex items-center gap-3">
                  <span className="text-xl font-bold text-slate-900">{planData.extra_checkins_used || 0}<span className="text-slate-400 font-normal">/{planData.extra_checkins_limit || 0}</span></span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Location info */}
          {userLocation && (
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <MapPin className="w-3.5 h-3.5" />
              <span>Mostrando academias num raio de 100 km da sua localização</span>
            </div>
          )}

          {/* Search */}
          {(primaryGym ? otherGyms : gyms).length > 3 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nome, cidade ou modalidade..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="gym-search-input"
                className="pl-10"
              />
            </div>
          )}

          {/* Gym list with full profiles */}
          {filteredGyms.length > 0 && (
            <div>
              <h2 className="text-base font-bold text-slate-900 mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {primaryGym ? 'Outras Academias (Check-in Extra)' : 'Academias Disponíveis'}
                <span className="text-slate-400 font-normal ml-2">({filteredGyms.length})</span>
              </h2>
              <div className="space-y-3">
                {filteredGyms.map((gym) => {
                  const compat = gymAcceptsMyPlan(gym);
                  const blockedNoPlan = !hasActivePlan;
                  const blocked = blockedNoPlan || !compat;
                  return (
                  <Card key={gym.gym_id} className={`border-slate-100 transition-all overflow-hidden ${selectedGym === gym.gym_id ? 'ring-2 ring-blue-400 border-blue-200' : blocked ? 'opacity-70' : 'hover:border-slate-200'}`} data-testid={`gym-card-${gym.gym_id}`}>
                    <CardContent className="p-0">
                      <button
                        onClick={() => handleSelectGym(gym)}
                        className="w-full text-left p-4"
                        data-testid={`gym-select-${gym.gym_id}`}
                      >
                        <div className="flex items-start gap-4">
                          {/* Gym photo */}
                          {gym.photo_path ? (
                            <img src={getImageUrl(gym.photo_path)} alt={gym.name} className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <Dumbbell className="w-8 h-8 text-slate-300" />
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                              <h3 className="font-bold text-slate-900 text-sm sm:text-base truncate" style={{ fontFamily: 'Chivo, sans-serif' }}>{gym.name}</h3>
                              {blockedNoPlan ? (
                                <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid={`gym-no-plan-${gym.gym_id}`}>
                                  Compre um plano
                                </span>
                              ) : compat ? (
                                <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full" data-testid={`gym-compat-${gym.gym_id}`}>
                                  Compatível
                                </span>
                              ) : (
                                <span className="text-[10px] font-bold uppercase tracking-wide bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full" data-testid={`gym-incompat-${gym.gym_id}`}>
                                  Não aceita seu plano
                                </span>
                              )}
                            </div>

                            {/* Address */}
                            {(gym.address || gym.neighborhood) && (
                              <p className="text-xs text-slate-500 mt-0.5 truncate">
                                {gym.address || `${gym.neighborhood}, ${gym.city || ''}`}
                                {gym.city && gym.state ? ` — ${gym.city}/${gym.state}` : ''}
                              </p>
                            )}

                            {/* Distance + Google Maps link */}
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              {gym.distance_km != null && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full" data-testid={`gym-distance-${gym.gym_id}`}>
                                  <MapPin className="w-3 h-3" /> {gym.distance_km} km
                                </span>
                              )}
                              {(gym.address || gym.city) && (
                                <a
                                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([gym.address, gym.neighborhood, gym.city, gym.state].filter(Boolean).join(', '))}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 font-medium"
                                  data-testid={`gym-maps-link-${gym.gym_id}`}
                                >
                                  <Navigation className="w-3 h-3" /> Ver no Maps
                                </a>
                              )}
                            </div>

                            {/* Opening hours */}
                            {gym.opening_hours && (
                              <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3" /> {gym.opening_hours}</p>
                            )}

                            {/* Modalities tags */}
                            {gym.modalities?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {gym.modalities.slice(0, 5).map(m => (
                                  <span key={m} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-medium">{m}</span>
                                ))}
                                {gym.modalities.length > 5 && <span className="text-[10px] text-slate-400">+{gym.modalities.length - 5}</span>}
                              </div>
                            )}

                            {/* Accepted plans */}
                            {gym.accepted_plans?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {gym.accepted_plans.map(p => (
                                  <span key={p} className="text-[10px] bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded font-medium capitalize">{p.replace('_', ' ')}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>

                      {/* Expanded details when selected */}
                      {selectedGym === gym.gym_id && (
                        <div className="px-4 pb-4 pt-0 border-t border-slate-100 space-y-3">
                          {/* Gallery */}
                          {gym.gallery_images?.length > 0 && (
                            <div className="flex gap-2 overflow-x-auto pt-3 pb-1">
                              {gym.gallery_images.map((img, i) => (
                                <img key={img || i} src={getImageUrl(img)} alt="" className="w-24 h-16 rounded-lg object-cover flex-shrink-0 border border-slate-100" />
                              ))}
                            </div>
                          )}

                          {/* Contact */}
                          <div className="flex flex-wrap gap-4 text-xs text-slate-500 pt-2">
                            {gym.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {gym.phone}</span>}
                            {gym.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {gym.email}</span>}
                          </div>

                          {/* Modality selection for check-in */}
                          {gym.modalities?.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-slate-600 mb-2">Selecione a modalidade:</p>
                              <div className="flex flex-wrap gap-2">
                                {gym.modalities.map(mod => (
                                  <Button key={mod} variant={selectedModality === mod ? 'default' : 'outline'} size="sm"
                                    onClick={() => setSelectedModality(selectedModality === mod ? null : mod)}
                                    className={selectedModality === mod ? 'bg-emerald-600 hover:bg-emerald-700 text-white text-xs' : 'text-xs'}
                                    data-testid={`modality-select-${mod}`}>
                                    {selectedModality === mod && <Check className="w-3 h-3 mr-1" />}{mod}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Action buttons */}
                          <div className="flex gap-2 pt-1">
                            {!primaryGym && (
                              <Button size="sm" variant="outline" onClick={() => handleBindGym(gym.gym_id)}
                                disabled={bindingGym !== null || blocked} data-testid={`bind-gym-${gym.gym_id}`}
                                className="text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50 flex-1 disabled:opacity-50">
                                {bindingGym === gym.gym_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                                {blockedNoPlan ? 'Compre um plano' : compat ? 'Vincular como Principal' : 'Plano incompatível'}
                              </Button>
                            )}
                            <Button size="sm" onClick={() => generateCode(gym.gym_id)}
                              disabled={generating || blocked || (gym.modalities?.length > 0 && !selectedModality)}
                              data-testid={`extra-checkin-${gym.gym_id}`}
                              className={`text-xs gap-1 flex-1 ${primaryGym ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-600 hover:bg-emerald-700'} text-white disabled:opacity-50`}>
                              {generating ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Target className="w-3 h-3" />}
                              {blockedNoPlan ? 'Escolher plano' : compat ? (primaryGym ? 'Check-in Extra' : 'Gerar Check-in') : 'Incompatível'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );})}
              </div>
            </div>
          )}

          {filteredGyms.length === 0 && gyms.length > 0 && searchQuery && (
            <div className="text-center py-8 text-slate-500 text-sm">
              Nenhuma academia encontrada para &quot;{searchQuery}&quot;
            </div>
          )}

          {gyms.length === 0 && (
            <Card className="border-slate-100">
              <CardContent className="p-12 text-center">
                <Dumbbell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Nenhuma academia disponível</h3>
                <p className="text-slate-500 text-sm">
                  {userLocation
                    ? 'Não há academias que aceitem seu plano num raio de 100 km.'
                    : 'Ative a localização para ver academias próximas ou não há academias para o seu plano.'}
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Bind Gym Modality Selection Dialog */}
      {showBindConfirm && (() => {
        const gym = gyms.find(g => g.gym_id === showBindConfirm);
        if (!gym) return null;
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bind-modality-dialog">
            <Card className="max-w-md w-full border-0 shadow-2xl">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Vincular a {gym.name}
                </h3>

                {/* Warning */}
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Atenção!</p>
                      <p className="text-xs text-amber-700 mt-1">Após vincular, as modalidades selecionadas <strong>não poderão ser alteradas</strong> durante os próximos 30 dias. Escolha com cuidado.</p>
                    </div>
                  </div>
                </div>

                {/* Modality selection */}
                <p className="text-sm font-semibold text-slate-700 mt-4 mb-2">Selecione as modalidades que vai frequentar:</p>
                <div className="flex flex-wrap gap-2">
                  {(gym.modalities || []).map(mod => {
                    const selected = bindModalities.includes(mod);
                    return (
                      <Button key={mod} variant={selected ? 'default' : 'outline'} size="sm"
                        onClick={() => {
                          if (selected) setBindModalities(prev => prev.filter(m => m !== mod));
                          else setBindModalities(prev => [...prev, mod]);
                        }}
                        className={selected ? 'bg-emerald-600 hover:bg-emerald-700 text-white text-xs' : 'text-xs'}
                        data-testid={`bind-modality-${mod}`}>
                        {selected && <Check className="w-3 h-3 mr-1" />}{mod}
                      </Button>
                    );
                  })}
                </div>

                <div className="flex gap-3 mt-5">
                  <Button variant="outline" className="flex-1" onClick={() => { setShowBindConfirm(null); setBindModalities([]); }}>Cancelar</Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={bindModalities.length === 0 || bindingGym !== null}
                    onClick={confirmBindWithModalities} data-testid="confirm-bind-btn">
                    {bindingGym ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                    Confirmar Vínculo
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      })()}
    </div>
  );
};


// Student Profile Page Component
const StudentProfilePage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  // Extract student_id from user_id (format: student_stud_xxx -> stud_xxx)
  const studentId = user?.student_id || (user?.user_id?.startsWith('student_') ? user.user_id.slice(8) : null);

  useEffect(() => {
    if (studentId) {
      axios.get(`${API}/students/${studentId}/profile`)
        .then(r => { setProfile(r.data); setEditData(r.data); })
        .catch(() => toast.error('Erro ao carregar perfil'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [studentId]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande (máx 5MB)');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/students/${studentId}/profile-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile(prev => ({ ...prev, profile_photo: res.data.profile_photo }));
      toast.success('Foto atualizada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar foto');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/students/${studentId}/profile`, {
        name: editData.name,
        phone: editData.phone,
        cellphone: editData.cellphone,
        birth_date: editData.birth_date,
      });
      setProfile(prev => ({ ...prev, ...editData }));
      setEditing(false);
      toast.success('Perfil atualizado!');
    } catch {
      toast.error('Erro ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!profile) return <div className="text-center py-12 text-slate-500">Perfil não encontrado</div>;

  return (
    <div className="space-y-6" data-testid="student-profile-section">
      <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Meu Perfil</h2>

      {/* Photo */}
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            <div className="relative group">
              {profile.profile_photo ? (
                <img
                  src={getImageUrl(profile.profile_photo)}
                  alt="Foto de perfil"
                  className="w-24 h-24 rounded-full object-cover border-4 border-slate-200"
                  data-testid="student-profile-photo"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-slate-200 flex items-center justify-center border-4 border-slate-100" data-testid="student-profile-photo-placeholder">
                  <User className="w-10 h-10 text-slate-400" />
                </div>
              )}
              <label
                className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                data-testid="student-photo-upload-label"
              >
                {uploading ? (
                  <Loader2 className="w-6 h-6 animate-spin text-white" />
                ) : (
                  <Camera className="w-6 h-6 text-white" />
                )}
                <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" disabled={uploading} data-testid="student-photo-upload-input" />
              </label>
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">{profile.name}</h3>
              <p className="text-sm text-slate-500">{profile.email || 'Sem e-mail'}</p>
              <p className="text-xs text-slate-400 mt-1">Passe o mouse na foto para trocar</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Dados Pessoais</span>
            {!editing ? (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="student-profile-edit-btn">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setEditing(false); setEditData(profile); }}>Cancelar</Button>
                <Button size="sm" onClick={handleSaveProfile} disabled={saving} data-testid="student-profile-save-btn" className="bg-slate-900 text-white">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                  Salvar
                </Button>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-500 text-xs">Nome</Label>
              {editing ? (
                <Input value={editData.name || ''} onChange={e => setEditData(d => ({...d, name: e.target.value}))} data-testid="student-profile-name-input" />
              ) : (
                <p className="text-sm font-medium text-slate-900">{profile.name}</p>
              )}
            </div>
            <div>
              <Label className="text-slate-500 text-xs">Telefone</Label>
              {editing ? (
                <Input value={editData.cellphone || ''} onChange={e => setEditData(d => ({...d, cellphone: e.target.value}))} data-testid="student-profile-phone-input" />
              ) : (
                <p className="text-sm font-medium text-slate-900">{profile.cellphone || profile.phone || '—'}</p>
              )}
            </div>
            <div>
              <Label className="text-slate-500 text-xs">Data de Nascimento</Label>
              {editing ? (
                <Input type="date" value={editData.birth_date || ''} onChange={e => setEditData(d => ({...d, birth_date: e.target.value}))} data-testid="student-profile-birthdate-input" />
              ) : (
                <p className="text-sm font-medium text-slate-900">{profile.birth_date || '—'}</p>
              )}
            </div>
            <div>
              <Label className="text-slate-500 text-xs">Email</Label>
              <p className="text-sm font-medium text-slate-900">{profile.email || '—'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


// Main Student Dashboard Component
const StudentDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [studentPlanData, setStudentPlanData] = useState(null);
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/students/my-plan`)
      .then(res => setStudentPlanData(res.data))
      .catch(e => console.error('Failed to load plan:', e));
    registerPushSubscription();
  }, []);

  // Auto-open onboarding for first-time Personal-plan users
  useEffect(() => {
    if (!user?.user_id || !studentPlanData?.plan) return;
    if (!studentPlanData.plan.startsWith('personal_') &&
        studentPlanData.plan !== 'vital_ultra' &&
        studentPlanData.plan !== 'vital_action') return;
    try {
      const seenKey = `vital_onboarding_personal_${user.user_id}`;
      if (!localStorage.getItem(seenKey)) {
        // small delay so the dashboard renders first
        const t = setTimeout(() => setOnboardingOpen(true), 500);
        return () => clearTimeout(t);
      }
    } catch (_) { /* localStorage disabled */ }
  }, [user?.user_id, studentPlanData]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isPersonalPlan = typeof studentPlanData?.plan === 'string' && (
    studentPlanData.plan.startsWith('personal_') ||
    studentPlanData.plan === 'vital_ultra' ||
    studentPlanData.plan === 'vital_action'
  );
  const isPremiumBenefitsPlan = typeof studentPlanData?.plan === 'string' && (
    studentPlanData.plan === 'vital_ultra' || studentPlanData.plan === 'vital_action'
  );

  const navItems = [
    { path: '/student', label: t('studentNav.checkin'), mobileLabel: t('studentNav.checkin'), icon: <Home className="w-5 h-5" /> },
    { path: '/student/history', label: t('studentNav.history'), mobileLabel: t('studentNav.history'), icon: <Calendar className="w-5 h-5" /> },
    { path: '/student/evolution', label: t('studentNav.evolution'), mobileLabel: t('studentNav.evolution'), icon: <TrendingUp className="w-5 h-5" /> },
    { path: '/student/coparticipation', label: t('studentNav.coparticipation'), mobileLabel: t('studentNav.coparticipation'), icon: <Users className="w-5 h-5" /> },
    ...(isPremiumBenefitsPlan ? [
      { path: '/student/benefits', label: 'Benefícios', mobileLabel: 'Saúde', icon: <Sparkles className="w-5 h-5" /> },
    ] : []),
    ...(isPersonalPlan ? [
      { path: '/student/professionals', label: 'Profissionais', mobileLabel: 'Profis', icon: <Stethoscope className="w-5 h-5" /> },
      { path: '/student/my-vitalpass', label: 'VITALPASS', mobileLabel: 'Pass', icon: <Ticket className="w-5 h-5" /> },
    ] : []),
    { path: '/student/profile', label: t('studentNav.profile'), mobileLabel: t('studentNav.profile'), icon: <User className="w-5 h-5" /> },
    { path: '/student/contact', label: t('studentNav.contact'), mobileLabel: t('studentNav.contact'), icon: <MessageCircle className="w-5 h-5" /> }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-center" />
      
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 z-40 hidden lg:block safe-top">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-8">
            <Activity className="w-8 h-8 text-emerald-500" />
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Vital
            </span>
          </div>
          
          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/student'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-emerald-500 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`
                }
              >
                {item.icon}
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Personal onboarding replay button */}
          {isPersonalPlan && (
            <button
              onClick={() => setOnboardingOpen(true)}
              data-testid="open-onboarding-tour"
              className="mt-4 w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 text-teal-800 hover:from-teal-100 hover:to-emerald-100 transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Ver tutorial VITALPASS
            </button>
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            {user?.profile_photo ? (
              <img src={getImageUrl(user.profile_photo)} alt="" className="w-10 h-10 rounded-full object-cover border border-slate-200" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <span className="text-emerald-600 font-semibold">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500">{t('studentDash.roleLabel')}</p>
            </div>
          </div>
          <div className="mb-3">
            <LanguageSwitcher />
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('common.logout')}
          </Button>
          <div className="mt-2 flex justify-center">
            <NotificationBell />
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden glass fixed top-0 w-full z-50 border-b border-slate-200/50 safe-top">
        <div className="px-4 h-14 flex items-center justify-between safe-x">
          <div className="flex items-center gap-2">
            <Activity className="w-6 h-6 text-emerald-500" />
            <span className="font-bold text-slate-900">Vital</span>
          </div>
          <div className="flex items-center gap-1">
            <LanguageSwitcher />
            <NotificationBell />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                {user?.profile_photo ? (
                  <img src={getImageUrl(user.profile_photo)} alt="" className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <span className="text-emerald-600 font-semibold text-sm">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                {t('common.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pb-20 lg:pb-0" style={{ paddingTop: 'calc(3.5rem + env(safe-area-inset-top, 0px))' }}>
        <div className="p-4 sm:p-6 lg:p-8 lg:pt-8">
          <div className="lg:pt-0">
          <PlanBanner planData={studentPlanData} variant="student" className="mb-6" />
          <Routes>
            <Route index element={<CheckInPage />} />
            <Route path="history" element={<HistoryPage />} />
            <Route path="evolution" element={<EvolutionPage />} />
            <Route path="coparticipation" element={<CoparticipationPage />} />
            <Route path="profile" element={<StudentProfilePage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="professionals" element={<ProfessionalNetworkPage />} />
            <Route path="my-vitalpass" element={<MyVitalPassPage />} />
            <Route path="benefits" element={<BenefitsPage />} />
          </Routes>
          </div>
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40 safe-bottom safe-x">
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/student'}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-1 py-2 min-w-0 ${
                  isActive ? 'text-emerald-500' : 'text-slate-500'
                }`
              }
            >
              {item.icon}
              <span className="text-[10px] leading-tight truncate max-w-[60px] text-center lg:hidden">{item.mobileLabel}</span>
              <span className="text-xs hidden lg:inline">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Personal Onboarding Tour */}
      {isPersonalPlan && (
        <PersonalOnboardingTour
          open={onboardingOpen}
          onClose={() => setOnboardingOpen(false)}
          userId={user?.user_id}
        />
      )}
    </div>
  );
};

export default StudentDashboard;
